import type { MenuScreen, Scene } from "./types";

export type ScreenFactory<TContext> = () => MenuScreen<TContext>;

export class MenuRouter<TContext> {
  private readonly factories = new Map<Scene, ScreenFactory<TContext>>();
  private current: { scene: Scene; screen: MenuScreen<TContext> } | null = null;
  private readonly root: HTMLElement;
  private readonly ctx: TContext;

  constructor(root: HTMLElement, ctx: TContext) {
    this.root = root;
    this.ctx = ctx;
  }

  register(scene: Scene, factory: ScreenFactory<TContext>): void {
    this.factories.set(scene, factory);
  }

  show(scene: Scene): void {
    if (this.current?.scene === scene) return;
    this.hide();
    const factory = this.factories.get(scene);
    if (!factory) {
      throw new Error(`No screen registered for scene "${scene}"`);
    }
    const screen = factory();
    screen.mount(this.root, this.ctx);
    this.current = { scene, screen };
  }

  hide(): void {
    if (!this.current) return;
    this.current.screen.unmount();
    while (this.root.firstChild) {
      this.root.removeChild(this.root.firstChild);
    }
    this.current = null;
  }

  update(): void {
    this.current?.screen.update?.(this.ctx);
  }

  remountCurrent(): void {
    if (!this.current) return;
    const scene = this.current.scene;
    this.hide();
    this.show(scene);
  }

  getCurrentScene(): Scene | null {
    return this.current?.scene ?? null;
  }
}
